// US-only phone normalization for outbound calling.
//
// BizForge OS is US-only, so the softphone accepts bare 10-digit numbers as well
// as 11-digit (leading 1) and E.164 (+1…) inputs, and normalizes them all to
// E.164. Non-US / non-normalizable input returns null.

const E164_REGEX = /^\+[1-9]\d{7,14}$/
const E164_US_REGEX = /^\+1[2-9]\d{9}$/

/**
 * FND-017: strip non-ASCII characters before parsing. Without this, Unicode
 * digit lookalikes (Arabic-Indic ٥, fullwidth ５, mathematical 𝟱, etc.) slip
 * through and a non-dialable value gets sent to the backend or to the dialer.
 * Returns null when the input is empty or contains zero ASCII digits after
 * stripping.
 */
export function sanitizePhoneInput(input: string | null | undefined): string | null {
  if (input == null) return null
  // eslint-disable-next-line no-control-regex
  const ascii = input.replace(/[^\x00-\x7F]/g, '').trim()
  if (!ascii) return null
  if (!/[0-9]/.test(ascii)) return null
  return ascii
}

/**
 * Normalize user input to an E.164 US phone number, or null if it isn't one.
 *
 *   "5551234567"        → "+15551234567"
 *   "(555) 123-4567"    → "+15551234567"
 *   "1-555-123-4567"    → "+15551234567"
 *   "+15551234567"      → "+15551234567"
 *   "555-1234"          → null    (too short)
 *   "+447911123456"     → null    (non-US)
 *   "٥٥٥-١٢٣-٤٥٦٧"      → null    (Unicode digit lookalikes; FND-017)
 *
 * Rules:
 *  - Strip non-ASCII characters first; reject if zero ASCII digits remain
 *  - Strip remaining non-digit characters except a leading "+"
 *  - If the input already starts with "+", it must validate as E.164 AND
 *    be a US number (+1 followed by a valid NANP area code)
 *  - 10 digits → prepend "+1"
 *  - 11 digits starting with "1" → prepend "+"
 *  - Anything else → null
 *  - The final value must match the US NANP pattern (area code + exchange
 *    cannot start with 0 or 1 per NANP rules)
 */
export function normalizeUsPhone(input: string | null | undefined): string | null {
  const trimmed = sanitizePhoneInput(input)
  if (trimmed === null) return null

  if (trimmed.startsWith('+')) {
    const cleaned = '+' + trimmed.slice(1).replace(/\D/g, '')
    if (!E164_REGEX.test(cleaned)) return null
    return E164_US_REGEX.test(cleaned) ? cleaned : null
  }

  const digits = trimmed.replace(/\D/g, '')
  let e164: string
  if (digits.length === 10) e164 = `+1${digits}`
  else if (digits.length === 11 && digits.startsWith('1')) e164 = `+${digits}`
  else return null

  return E164_US_REGEX.test(e164) ? e164 : null
}

/**
 * True when input can be dialed as a US phone number. Convenience wrapper.
 */
export function isDialableUsPhone(input: string | null | undefined): boolean {
  return normalizeUsPhone(input) !== null
}

/**
 * FND-017 + FND-018: mirror of the backend helper. Useful for any frontend
 * surface that needs the same display/normalized split (e.g. optimistic
 * caching keyed by normalizedPhone). Algorithm is identical to the backend.
 */
export function preparePhoneForStorage(phone: string | null | undefined): {
  display: string | null
  normalized: string | null
} {
  const normalized = normalizeUsPhone(phone)
  if (normalized) {
    return { display: normalized, normalized }
  }
  const sanitized = sanitizePhoneInput(phone)
  return { display: sanitized, normalized: null }
}
